import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPublicProject } from "rc-app/api/shareLinkActionsApi";
import {
  getGuidedRoom,
  getGuidedRoomRoster,
  issueGuestPass,
  leaveGuidedRoom,
} from "rc-app/api/guidedRoomActionsApi";
import { useViewportInsets } from "rc-app/hooks/useViewportInsets";
import { useGuidedRoomVoice } from "rc-app/hooks/useGuidedRoomVoice";
import { connectionNotice } from "rc-app/utils/guidedRoomVoice";
import { useGuidedRoomRelay } from "rc-app/hooks/useGuidedRoomRelay";
import { canvasUrl, primarySource } from "rc-app/utils/guidedRoomCanvas";
import { useGuidedRoomChat } from "rc-app/hooks/useGuidedRoomChat";
import { chatTime } from "rc-app/utils/guidedRoomChat";
import { waLink, cp, firstNameOf, isRoomGone } from "rc-app/utils/guidedRoomFlow";
import {
  possessionLabel,
  locationLabel,
  configurationLabel,
  highlightsFrom,
  highlightColumns,
  staticMapUrl,
  unitRowsFrom,
  amenityGroupsFrom,
  amenityCount,
  familyQuota,
  familyNote,
  peopleRows,
  knownRoster,
  initialsOf,
  elapsedLabel,
  canIssueFamilyPass,
  type PersonRow,
} from "rc-app/utils/guidedRoomGuest";
import "./GuidedRoomGuest.css";

/* ── Tour Room (Guest) ─────────────────────────────────────────────────
   Route: /tour/:roomId    (PUBLIC — a buyer has no Keycloak account)

   Built to docs/product specs/tour_room_guest/. The buyer watches a
   walkthrough the host drives. What they control is their own mic and
   camera, their raised hand, chat, who else they bring in, and how much
   of the project they read while it runs.

   ── HOW SOMEONE GETS HERE ──
   GuidedRoomJoin handles the funnel (landing → OTP → lobby) and sends
   them here once ADMITTED, with the participant credential in storage.
   Landing here without one is not an error to explain — it's someone
   who followed a stale link — so we bounce to the join page rather than
   showing a dead screen.

   ── AUTH ──
   Every call is authorised by the participant credential, never by a
   Keycloak token. That is the whole reason this is a separate page from
   GuidedRoomHost rather than a mode of it: the two authorise
   differently, and mixing them would eventually leak a host-only call
   onto a public screen.

   ── WHAT IS REAL AND WHAT IS WAITING ──
   Real: voice, the people list, the project panel, the family link and
   its quota, the friends link, leaving, and the raised hand — which
   rides the sync relay rather than a data channel Agora's web SDK
   doesn't have. See utils/guidedRoomRelay.ts for why the frame looks
   the way it does.
   Also real: the walkthrough canvas — the engine embedded with
   role=viewer, following the host's camera over the same relay. It never
   needed ws_token; that relay authenticates nobody. See
   utils/guidedRoomCanvas.ts, including why this URL carries less than
   ProjectDetailsV3's (it must not open a duplicate vr_session).

   Waiting: chat, and the token that would make any of this safe. Chat
   could ride the same relay and deliberately does not — a hand is a
   boolean worth nothing to an eavesdropper, and what a buyer types about
   their budget is not. It stays disabled with the reason attached rather
   than faked: a chat box that silently talks to nobody is worse than one
   that says it isn't ready.

   ── EVERY NUMBER IS DERIVED ──
   The reference HTML is full of confident facts (12.4 acres, 7 towers,
   All 29 amenities) about a project nobody here is looking at. Not one
   of them is hard-coded; see utils/guidedRoomGuest.ts, which drops any
   cell the data can't fill rather than defaulting it. A buyer reading
   invented facts about a home is the worst failure this screen has. */

/* The join page files a credential under `${CRED_KEY}:${joinCode-or-passToken}`
   because one phone can hold credentials for several tours and the
   `/pass/<token>` door carries no join code. This page knows only the
   ROOM, so it sweeps by value rather than guessing the suffix — the same
   approach the host page uses to release its active-room claim.

   Sweeping also means a family link and a lead link that landed in the
   same browser both resolve correctly: we match on room_id, not on which
   door was used. */
const CRED_KEY = "guidedRoom:credential:v1";
const LOBBY_POLL_MS = 5000;

/* On a phone the rail is a drawer over the walkthrough rather than a column
   beside it, so it has to start closed — otherwise a buyer who has just been
   admitted sees a panel of project text where the home should be. Anywhere
   wider there is room for both and it starts open. */
const PHONE_MQ = "(max-width: 699px)";
const phoneWidth = (): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia(PHONE_MQ).matches;

const findCredential = (roomId: string): { key: string; cred: Credential } | null => {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(`${CRED_KEY}:`)) continue;
      try {
        const c = JSON.parse(localStorage.getItem(k) || "null");
        if (c?.session_id && c?.session_secret && String(c.room_id) === String(roomId)) {
          return { key: k, cred: c };
        }
      } catch { /* malformed entry — keep looking */ }
    }
  } catch { /* storage blocked */ }
  return null;
};
type Tab = "about" | "invite" | "people" | "chat";
type Credential = { session_id: string; session_secret: string; room_id?: string; join_code?: string; invited?: boolean; role?: string };

/* ── icons ─────────────────────────────────────────────────────────── */
const S = (p: any) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={p.w || 1.9}
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{p.children}</svg>
);
const IcBack = () => <S w={2}><path d="M19 12H5M11 6l-6 6 6 6" /></S>;
const IcPin = () => <S w={2}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></S>;
const IcMic = () => <S><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></S>;
const IcMicOff = () => <S><path d="M15 9.3V6a3 3 0 0 0-5.9-.7M9 9v5a3 3 0 0 0 5.1 2.1" /><path d="M5 11a7 7 0 0 0 10.3 6.2M19 11a7 7 0 0 1-.5 2.6M12 18v3" /><path d="M4 4l16 16" /></S>;
const IcCam = () => <S><path d="M15 10l4.5-2.5v9L15 14" /><rect x="3" y="6" width="12" height="12" rx="2" /></S>;
const IcHand = () => <S w={1.8}><path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12m0-1.5a1.5 1.5 0 0 1 3 0V12m0-1a1.5 1.5 0 0 1 3 0v4a6 6 0 0 1-6 6h-1a6 6 0 0 1-6-6v-2a1.5 1.5 0 0 1 3 0" /></S>;
const IcSkyline = () => <S w={1.7}><path d="M3.5 20.5h17" /><path d="M5 20.5V9.4a.8.8 0 0 1 .8-.8h4.4a.8.8 0 0 1 .8.8v11.1" /><path d="M11 20.5V5.3a.8.8 0 0 1 .8-.8h5.4a.8.8 0 0 1 .8.8v15.2" /><path d="M13.4 8.4h2.4M13.4 12h2.4M13.4 15.6h2.4M7.2 12.4h1.6M7.2 16h1.6" /></S>;
const IcInvite = () => <S><circle cx="9" cy="8" r="3.2" /><path d="M3 19a6 6 0 0 1 12 0" /><path d="M18 8v6M15 11h6" /></S>;
const IcPeople = () => <S w={1.8}><circle cx="9" cy="8" r="3.2" /><path d="M3 19a6 6 0 0 1 12 0" /><path d="M16 5.5a3.2 3.2 0 0 1 0 6M18 19a6 6 0 0 0-2.5-4.9" /></S>;
const IcChat = () => <S w={1.8}><path d="M20 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" /></S>;
const IcLeave = () => <S w={2}><path d="M10 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /><path d="M16 17l5-5-5-5M21 12H9" /></S>;
const IcClose = () => <S w={2.4}><path d="M6 6l12 12M18 6L6 18" /></S>;
const IcPlay = () => <S w={1.6}><path d="M9 8l7 4-7 4V8Z" /><circle cx="12" cy="12" r="9" /></S>;
const IcCal = () => <S><rect x="3.5" y="5" width="17" height="15.5" rx="2" /><path d="M3.5 10h17M8 3.5v3M16 3.5v3" /></S>;
const IcShield = () => <S><path d="M12 3l7.5 3v6c0 4.6-3.2 7.6-7.5 9-4.3-1.4-7.5-4.4-7.5-9V6L12 3Z" /><path d="M9 12l2 2 4-4" /></S>;
const IcExpand = () => <S w={2}><path d="M14 4h6v6M20 4l-8.5 8.5" /><path d="M18 14v5a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 19V7.5A1.5 1.5 0 0 1 5 6h5" /></S>;
const IcFullscreen = () => <S w={2}><path d="M8 3.5H5A1.5 1.5 0 0 0 3.5 5v3M16 3.5h3A1.5 1.5 0 0 1 20.5 5v3M8 20.5H5A1.5 1.5 0 0 1 3.5 19v-3M16 20.5h3a1.5 1.5 0 0 0 1.5-1.5v-3" /></S>;
const IcExitFullscreen = () => <S w={2}><path d="M8 3.5v3A1.5 1.5 0 0 1 6.5 8h-3M16 3.5v3A1.5 1.5 0 0 0 17.5 8h3M8 20.5v-3A1.5 1.5 0 0 0 6.5 16h-3M16 20.5v-3a1.5 1.5 0 0 1 1.5-1.5h3" /></S>;
const IcCopy = () => <S w={2}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></S>;
const IcTick = () => <S w={2}><path d="M5 12.5l4 4 10-10" /></S>;
const IcInfo = () => <S w={2}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></S>;
const IcEye = () => <S><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" /></S>;
const IcDown = () => <S><path d="M12 4v10M8 10.5l4 4 4-4" /><path d="M4.5 19.5h15" /></S>;
const IcSend = () => <S w={2}><path d="M4 12l16-8-6 16-2.5-6.5L4 12Z" /></S>;
const IcFamily = () => <S><circle cx="9" cy="7.5" r="3" /><path d="M3 19a6 6 0 0 1 12 0" /><circle cx="17.5" cy="9.5" r="2.2" /><path d="M15 19a5 5 0 0 1 6.5-4.4" /></S>;
const IcLink = () => <S><path d="M10.6 13.4a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M13.4 10.6a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 0 0 5.7 5.7l1-1" /></S>;
const IcWa = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.35A10 10 0 1 0 12 2Zm5.05 14.18c-.22.6-1.27 1.16-1.76 1.2-.5.05-.96.07-1.5-.06a12.2 12.2 0 0 1-4.4-2.42 11.5 11.5 0 0 1-2.4-3.2c-.35-.7-.53-1.4-.53-2.02 0-.85.36-1.53.83-2 .2-.2.44-.3.66-.3h.48c.16 0 .35.02.5.36.18.42.63 1.53.68 1.64.06.11.09.24.02.38-.07.15-.13.24-.24.38l-.35.42c-.11.11-.23.24-.1.47.13.23.58.96 1.25 1.55.85.76 1.57 1 1.8 1.11.23.12.37.1.5-.06.14-.15.6-.7.76-.94.16-.24.32-.2.54-.12.22.08 1.4.66 1.64.78.24.12.4.18.46.28.06.1.06.6-.16 1.2Z" />
  </svg>
);

/* Only a LAST-RESORT default. The real number comes from guest_pass
   (`max`), so raising the quota is a backend change alone — nothing here
   needs editing or redeploying. Kept because the card renders before the
   pass has been fetched, and "0 of —" reads worse than "0 of 4". */
const MAX_FAMILY = 4;

/* `roomId` comes from the route on /tour/:roomId, but the buyer normally
   never goes there: the join page renders this component in place, so the
   URL stays visit.openskyvr.com/<code> for their whole visit. The prop is
   how it gets the room in that case. See GUIDED_ROOM_ADMISSION_MATRIX.md. */
type GuestProps = {
  roomId?: string;
  /* What the PUBLIC join view already told the join page. The room and
     lobby reads are staff-only (401 for a real buyer in a clean browser),
     so without this a buyer sees "This project" and no advisor name —
     which is how it looked until we finally tested outside a logged-in
     window. Not a workaround for the missing endpoints, just the part we
     can honestly fill in from data the buyer is already entitled to. */
  preview?: {
    project_name?: string;
    host_first_name?: string;
    /* Added to the public join view on 2026-08-27. This is what makes the
       walkthrough render for a buyer: the room read is still 401 for them,
       so `room.project_id` never arrives, and without this the canvas has
       no project and falls back to the placeholder card. */
    project_id?: string;
  } | null;
  /* The code this buyer's own link carried — the /visit/:joinCode they
     arrived on. See `joinCode` below for why the room can't supply it. */
  joinCode?: string;
  /* The name this person gave at the door. A last-resort fallback for the
     few seconds before their own lobby row arrives on the poll — without it
     anything they send in that window goes out labelled "Guest", which is
     how a real lead ends up appearing as GUEST beside their own words. */
  selfNameHint?: string;
};

const GuidedRoomGuest: React.FC<GuestProps> = ({ roomId: roomIdProp, preview, joinCode: joinCodeProp, selfNameHint }) => {
  const { roomId: roomIdParam = "" } = useParams();
  const roomId = roomIdProp || roomIdParam;
  const navigate = useNavigate();
  useViewportInsets();

  const [cred, setCred] = useState<Credential | null>(null);
  const [credChecked, setCredChecked] = useState(false);
  const [room, setRoom] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [admitted, setAdmitted] = useState<any[]>([]);
  const [gone, setGone] = useState(false);

  const [railOn, setRailOn] = useState(() => !phoneWidth());
  const [tab, setTab] = useState<Tab>("about");
  const [unitKey, setUnitKey] = useState<string>("");
  const [overlay, setOverlay] = useState<null | "map" | "amenities" | "brochure">(null);

  const [pass, setPass] = useState<any>(null);
  const [copied, setCopied] = useState<"" | "fam" | "raw">("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);

  /* ── FULL SCREEN ──
     The engine hides its own fullscreen button for a buyer in a guided tour
     (index.html: body[data-role="viewer"][data-session-mode="guided"]
     #btnFullscreen), so the host could go full screen and the buyer could
     not. This one is ours, on the dock, and it fullscreens the walkthrough
     area rather than the engine — so it can't change who drives the camera.

     "fake" is for iPhone Safari, which cannot fullscreen an element: the
     walkthrough is pinned over the whole viewport instead, and the exit
     button inside it is the way back, so it always renders then. */
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState<"off" | "native" | "fake">("off");

  const enterFullscreen = useCallback(async () => {
    const el: any = mountRef.current;
    if (!el) return;
    const request = el.requestFullscreen || el.webkitRequestFullscreen;
    if (request) {
      try {
        await request.call(el);
        return;
      } catch { /* refused (iOS, embedded contexts) — fall back below */ }
    }
    setFullscreen("fake");
  }, []);

  const exitFullscreen = useCallback(async () => {
    const d: any = document;
    if (d.fullscreenElement || d.webkitFullscreenElement) {
      try { await (d.exitFullscreen || d.webkitExitFullscreen).call(d); } catch { /* already out */ }
    }
    setFullscreen("off");
  }, []);

  useEffect(() => {
    const d: any = document;
    const onChange = () => {
      const active = !!(d.fullscreenElement || d.webkitFullscreenElement);
      setFullscreen((prev) => (active ? "native" : prev === "fake" ? "fake" : "off"));
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen((prev) => (prev === "fake" ? "off" : prev));
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  const [hand, setHand] = useState(false);
  const [tick, setTick] = useState(0);

  /* Credential first — everything else depends on it. */
  const credKeyRef = useRef<string>("");
  useEffect(() => {
    const found = findCredential(roomId);
    if (found) { credKeyRef.current = found.key; setCred(found.cred); }
    setCredChecked(true);
  }, [roomId]);

  /* No credential means a stale or forwarded /tour link. Send them
     through the front door instead of explaining; the join page knows
     how to turn a person into a participant and this page does not. */
  useEffect(() => {
    if (credChecked && !cred) navigate("/", { replace: true });
  }, [credChecked, cred, navigate]);

  const sid = cred?.session_id || "";
  const secret = cred?.session_secret || "";

  /* Voice. Same hook as the host; the backend decides from our presence
     record whether we may publish, so a lead gets a mic and a family
     guest gets listen-only without this page choosing. */
  const voice = useGuidedRoomVoice(roomId, cred, !!cred && !gone);

  /* Room, then project. The room carries only project_id, so the panel's
     content is a second hop — same endpoint the join page uses, so the
     buyer never sees the project described two different ways. */
  useEffect(() => {
    if (!roomId || !cred) return undefined;
    let cancelled = false;
    getGuidedRoom(roomId)
      .then((r: any) => {
        if (cancelled) return;
        setRoom(r);
        if (String(r?.state || r?.status || "").toUpperCase() === "ENDED") setGone(true);
      })
      .catch((e: any) => { if (!cancelled && isRoomGone(e?.response?.status)) setGone(true); });
    return () => { cancelled = true; };
  }, [roomId, cred]);

  /* ── WHY THE PREVIEW COMES SECOND BUT MATTERS MOST ──
     For a staff member the room read above succeeds and `room.project_id`
     is the answer. For an actual buyer it 401s — `views/guided_room/{id}`
     is still staff-only on stage as of 2026-08-27 — so `room` stays null
     and this would be "".

     The public join view now returns `project_id` (verified on stage the
     same day), and the join page already holds that response. So the
     buyer's walkthrough renders from the preview, not from the room. Both
     the panel and the canvas below hang off this one value; without the
     fallback a buyer gets a placeholder card and no walkthrough, which is
     exactly what the room is for. */
  const projectId = room?.project_id || preview?.project_id || "";
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    getPublicProject(projectId)
      .then((p: any) => { if (!cancelled) setProject(p?.project || p); })
      .catch(() => { /* panel degrades — see facts below */ });
    return () => { cancelled = true; };
  }, [projectId]);

  /* Roster poll drives the People tab and the family-arrival count. Also
     our end-of-tour signal: a removed participant stops appearing.

     ── WHY NOT THE LOBBY ──
     This polled `views/guided_room/{id}/lobby` until 2026-09-08. That
     endpoint is host-only, so for an actual buyer every poll 401'd, four
     seconds apart, for the length of the tour — one test session logged 57
     — and `admitted` stayed empty. The People tab then showed the host and
     you and nobody else: a buyer on a three-person tour could not see the
     third person.

     `roster` is the same question asked with the credential they hold. It
     returns ADMITTED only, never the WAITING queue, and it is PII-stripped
     backend-side, which is what makes it safe to show a buyer at all.

     The lobby is still the right call for the HOST — it carries the queue
     and the lead classification this deliberately does not. */
  useEffect(() => {
    if (!roomId || !cred || gone) return undefined;
    let stopped = false;
    const read = async () => {
      try {
        const l: any = await getGuidedRoomRoster(roomId, cred);
        if (stopped) return;
        setAdmitted(Array.isArray(l?.admitted) ? l.admitted : []);
      } catch (e: any) {
        /* 403 is "not admitted yet", not an error worth showing — the room
           is still there. Only a gone room ends the poll; anything else
           leaves the last good roster on screen and knownRoster() keeps the
           tab honest with the two people we can always name. */
        if (!stopped && isRoomGone(e?.response?.status)) setGone(true);
      }
    };
    void read();
    const id = setInterval(read, LOBBY_POLL_MS);
    return () => { stopped = true; clearInterval(id); };
  }, [roomId, cred, gone]);

  /* The header timer. One interval for the whole page rather than a
     ticking clock component — it also refreshes nothing else, so the
     re-render is cheap. */
  useEffect(() => {
    if (gone) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [gone]);

  /* The family pass. Idempotent server-side — asking again returns the
     SAME link plus its remaining count, so this is safe to call on mount
     and gives the quota bar something real to show immediately. */
  useEffect(() => {
    if (!roomId || !sid || !secret || gone) return undefined;
    let cancelled = false;
    issueGuestPass(roomId, { session_id: sid, session_secret: secret })
      .then((p: any) => { if (!cancelled) setPass(p); })
      .catch(() => { /* invite tab degrades to the friends link only */ });
    return () => { cancelled = true; };
  }, [roomId, sid, secret, gone]);

  /* ── derived ─────────────────────────────────────────────────────── */
  const facts = useMemo(() => {
    const p = project || {};
    return {
      name: String(p.project_name || room?.project_name || preview?.project_name || "This project"),
      location: locationLabel(p.address),
      configuration: configurationLabel(p.project_type_details),
      possession: possessionLabel(p.completion_date),
      rera: String(p.registration_number || "").trim(),
      highlights: highlightsFrom(p),
      units: unitRowsFrom(p),
      groups: amenityGroupsFrom(p),
      amenityTotal: amenityCount(p),
      brochure: String(p.project_brochure || "").trim(),
      geo: Array.isArray(p.geo_location) && p.geo_location.length === 2 ? p.geo_location : null,
      agency: String(p.agency_public?.name || "").trim(),
    };
  }, [project, room]);

  const selfRow = admitted.find((p: any) => String(p?.session_id) === String(sid));
  const hostRow = admitted.find((p: any) => String(p?.role || "").toLowerCase() === "host");
  const hostName = String(
    hostRow?.display_name || room?.host_name || preview?.host_first_name || "",
  ).trim();
  const selfName = String(selfRow?.display_name || "").trim();

  /* Only an INVITED lead may hand out family passes — the locked matrix in
     utils/guidedRoomGuest.ts. Fails closed, and the backend enforces the
     same rule; this only decides whether the card is worth showing. */
  const mayInviteFamily = canIssueFamilyPass(selfRow, room, cred);

  /* Chat over the voice channel — utils/guidedRoomChat.ts carries the
     reasoning. It follows voice: a buyer whose audio hasn't connected has
     no channel to send on, and the composer says so rather than swallowing
     what they typed. */
  const chat = useGuidedRoomChat({
    client: voice.client,
    sessionId: sid,
    displayName: selfName || selfNameHint || "Guest",
    role: "viewer",
    enabled: !gone,
    /* Also keeps each message we send — see the note in the hook. A second
       write, not the delivery path, so a buyer notices nothing if it fails. */
    credential: cred,
    roomId,
    /* Fills in the advisor on messages restored from history — the
       backend knows the host by token, so those rows carry no name. */
    hostName: hostName,
  });

  /* The walkthrough URL, or "" when this project has no meta model to
     render. Depends on the project fetch, so it's empty on first paint
     and the placeholder shows for that moment — which is correct: there
     is genuinely nothing to render yet. */
  const canvas = useMemo(
    () => canvasUrl(primarySource(project), {
      /* The Mongo id, never `project.project_id` — that field is the PRJ code
         ("PRJ78"). The host's canvas always had the real id; the buyer's had
         the code, so the engine could load the meta model (passed by its own
         id) but never found the project's digital twin. Found 2026-09-17. */
      projectId: String(projectId || ""),
      roomId,
      role: "viewer",
    }),
    [project, projectId, roomId],
  );

  /* The side channel that carries the raised hand. Separate socket from
     voice on purpose: a buyer who refused their microphone, or whose
     Agora token failed, can still ask a question this way. A guest only
     ever sends on it — the host is the one who listens. */
  const relay = useGuidedRoomRelay(roomId, sid, "viewer", {
    enabled: !!cred && !gone,
    displayName: selfName,
    /* Buys the ws_token. A buyer gets privileges [] — they may raise a
       hand but must never be able to drive the walkthrough. */
    credential: cred,
    /* The host answered us, so the button goes back to rest. Doing this
       from their side rather than making the buyer remember is what
       keeps the host's queue meaningful — see guidedRoomRelay.ts. */
    onAck: () => setHand(false),
  });

  /* The lobby is host-only, so for a real buyer `admitted` is always []
     — see knownRoster(), which supplies the two people we know for
     certain from the public preview and our own credential. */
  const people: PersonRow[] = useMemo(
    () => {
      const opts = {
        /* `selfName` comes from the lobby row, which a buyer cannot read,
           so it was always "" and every buyer saw themselves as the word
           "You" beside the tag "YOU". `selfNameHint` is what they typed at
           the door — the one name we hold without the room API. */
        hostName: hostName || "Your advisor",
        selfName: selfName || selfNameHint || "You",
        agencyName: facts.agency,
      };
      return knownRoster(peopleRows(admitted, sid, opts), sid, opts);
    },
    [admitted, sid, hostName, selfName, selfNameHint, facts.agency],
  );

  /* Family arrivals: guests admitted on OUR pass. Counted off the live
     roster rather than a local tally, so it stays right after a refresh
     and across devices. */
  const family = useMemo(
    () => admitted.filter((p: any) =>
      String(p?.role || "").toLowerCase() === "guest" && String(p?.invited_by || "") === String(sid)),
    [admitted, sid],
  );
  /* The larger of the two, not "pass first". `pass` is fetched once, when
     the link is made, so its `used` is 0 for the rest of the tour — on
     2026-09-11 Arjun's card listed four relatives "In the room" directly
     under a bar reading "0 of 4". The roster is live; the pass is a
     snapshot. Either can lag, neither can over-count. */
  const quotaUsed = Math.max(Number(pass?.used) || 0, family.length);
  const quota = familyQuota(quotaUsed, Number(pass?.max) || MAX_FAMILY);

  /* ── THE LINK A BUYER CAN PASS ON ──
     Same root cause as projectId above: `views/guided_room/{id}` is
     staff-only, so for an actual buyer `room` is null and this was
     always "". The Invite tab then rendered a card with an em dash where
     the link should be and both buttons dead — the one screen whose whole
     job is handing the link on.

     Three sources, in order of how much they can be trusted:
       room.join_code  — staff, whose room read succeeds
       joinCodeProp    — the URL this buyer is standing on, from the join
                         page that rendered us
       cred.join_code  — stored at verification, so a reload straight into
                         /tour/:roomId still has it

     A family guest who came through /pass/<token> has none of these, and
     that is not a failure: they were let in on someone else's pass and
     have no lead link of their own to give. The card hides. */
  const joinCode = String(room?.join_code || joinCodeProp || cred?.join_code || "");
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  /* ── PREFER THE SHORT CODE, THE MOMENT IT EXISTS ──
     `guest_pass` returns a JWT today, so this link is ~300 characters of
     base64 — see GUIDED_ROOM_FAMILY_PASS_CODE.md for why that matters
     (it reads as phishing in a forwarded WhatsApp message, it wraps and
     truncates, and the dots in a JWT look like a file request to an SPA
     router, which is how every /pass link came to render the marketing
     homepage).

     `guest_code` is the agreed replacement and both are meant to work at
     `guest/{code-or-token}/join` during the transition. Reading it here
     first means the day the backend ships it, the link shortens with no
     frontend release and no coordination — and until then this is exactly
     today's behaviour. Same reason the field names are read loosely:
     which key carries it is not worth a round trip. */
  const famCode = String(
    pass?.guest_code || pass?.code || pass?.short_code || "",
  ).trim();
  /* ── THE FAMILY LINK GOES THROUGH THE ROOM'S OWN DOOR ──
     `/pass/<code>` carries no join code, and every public description of a
     room is keyed on the join code — so a relative opening it saw "Your
     guided tour" with no project name and, worse, no recording consent
     notice, until AFTER they had already joined. A notice shown after
     someone is in the room is not consent.

     `/visit/<join_code>?g=<code>` is the same family flow — no OTP, name
     only, pass already approved — but the page can fetch the room's public
     preview before anything is tapped. Verified on stage 2026-09-11: the
     project name AND the consent notice both render on the first screen.

     Sixteen characters longer, and still short now that the family code is
     plain alphanumeric. `/pass/` stays as the fallback for the one case
     with no join code to hand. */
  const withDoor = (code: string) =>
    (joinCode ? `${origin}/visit/${joinCode}?g=${code}` : `${origin}/pass/${code}`);
  const famUrl = famCode
    ? withDoor(famCode)
    : (pass?.guest_token ? withDoor(pass.guest_token) : "");
  const rawUrl = joinCode ? `${origin}/visit/${joinCode}` : "";
  const elapsed = useMemo(() => elapsedLabel(room?.started_at, Date.now()), [room, tick]);

  const selectedUnit = unitKey || facts.units[0]?.key || "";

  /* Built once per project rather than per render — it is a URL with a key
     in it, and a new string every render would refetch the image. */
  const mapImage = useMemo(
    () => staticMapUrl(facts.geo, {
      key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
    }),
    [facts.geo],
  );

  /* ── actions ─────────────────────────────────────────────────────── */
  const copy = useCallback(async (text: string, which: "fam" | "raw") => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(""), 1600);
    } catch { /* clipboard blocked — the link is visible and selectable */ }
  }, []);

  /* Emoji built at runtime, never as literals — the build pipeline
     mangles non-ASCII in the bundle. See cp() in guidedRoomFlow. */
  const famMessage = () => [
    `${cp(0x1f3e1)} Join me on a live guided tour of ${facts.name}.`,
    "",
    "Tap the link and enter your name — that's it:",
    famUrl,
  ].join("\n");
  const rawMessage = () => [
    `${cp(0x1f4f9)} I'm on a live guided tour of ${facts.name}.`,
    "",
    `Open this, verify your mobile, and ${firstNameOf(hostName) || "the host"} will let you in:`,
    rawUrl,
  ].join("\n");

  const leave = useCallback(async () => {
    try { if (cred?.session_id) await leaveGuidedRoom(roomId, cred.session_id); }
    catch { /* leaving is best-effort; the room reaps us either way */ }
    /* Drop the credential so a Back or a re-opened link doesn't put them
       silently back into a tour they chose to leave. */
    try { if (credKeyRef.current) localStorage.removeItem(credKeyRef.current); } catch { /* ignore */ }
    navigate("/", { replace: true });
  }, [cred, roomId, navigate]);

  const openTab = (t: Tab) => {
    if (t === "chat") chat.markRead();
    if (railOn && tab === t) { setRailOn(false); return; }   // toggles, per spec
    setTab(t); setRailOn(true);
  };

  /* Having the tab open counts as reading it — a badge that survives an
     open panel reads as broken. */
  useEffect(() => {
    if (railOn && tab === "chat") chat.markRead();
  }, [railOn, tab, chat.messages.length]);      // eslint-disable-line react-hooks/exhaustive-deps

  /* Pin to the newest message, but only while chat is the visible tab: a
     message arriving behind the Project tab must not scroll something the
     buyer is in the middle of reading. */
  useEffect(() => {
    if (tab !== "chat" || !railOn) return;
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length, tab, railOn]);

  /* Same reasoning as the host's copy: the roster is the authority on who
     someone is, not the name their own browser knew when they typed. See the
     note in GuidedRoomHost. */
  const nameFor = (sessionId: string, fallback: string) => {
    const row = admitted.find((p: any) => String(p?.session_id) === String(sessionId));
    return String(row?.display_name || "").trim() || fallback || "Guest";
  };

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    const ok = await chat.send(draft);
    setSending(false);
    setSendFailed(!ok);
    /* Keep what they typed when it fails. Retyping a question because the
       network blipped is the fastest way to lose someone mid-tour. */
    if (ok) setDraft("");
  };

  /* Re-evaluate the rail when the breakpoint itself changes — a phone turned
     to landscape, a fold opened — rather than only at mount. Crossing into
     phone width closes it; crossing out opens it on whichever tab is active. */
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(PHONE_MQ);
    const onChange = () => setRailOn(!mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  /* Escape closes the topmost layer first — overlay before rail. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (overlay) { setOverlay(null); return; }
      if (railOn) setRailOn(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlay, railOn]);

  if (!credChecked) return <div className="grg-root" />;

  if (gone) {
    /* ── The last thing a buyer sees ─────────────────────────────────
       This was one sentence on an empty screen: the tour is over, ask
       your advisor if you want anything. A buyer who has just spent
       twenty minutes being walked through a home was handed a dead end,
       at the exact moment they are most interested.

       Everything they just saw is still published, so the page says so
       and offers it. It keeps the project's name and the agency's mark,
       because the buyer should leave remembering whose home it was. */
    /* The agent's own share link if the room carries one — their code, so
       this visit and anything that comes of it still credits the person
       who gave the tour. `my.<host>/s/<code>` is the same branded short
       link the Share button hands out.

       The plain project page is the fallback: correct, and unattributed.
       A room created before the backend carried `host_share_code` has
       none, and a code could not be resolved for every host. */
    const shareCode = String(room?.host_share_code || "").trim();
    const shortHost = (() => {
      try { return `my.${window.location.host}`; } catch (_e) { return ""; }
    })();
    const agencySlug = String(project?.agency_public?.slug || "").trim();
    const projectSlug = String(project?.slug || "").trim();
    const keepLookingHref = shareCode && shortHost
      ? `https://${shortHost}/s/${shareCode}?src=tour_ended`
      : agencySlug && projectSlug
        ? `/c/${agencySlug}/p/${projectSlug}`
        : "";

    /* Written as the buyer would write it, not as a brand would. They are
       forwarding a home to their own family — "we just saw this" carries
       further than anything we could say about ourselves. */
    const familyShareMessage = [
      `We just took a guided tour of ${facts.name || "this project"}`,
      facts.location ? ` in ${facts.location}` : "",
      ".",
      keepLookingHref ? `

Have a look: ${keepLookingHref.startsWith("http") ? keepLookingHref : window.location.origin + keepLookingHref}` : "",
    ].join("");

    return (
      <div className="grg-root">
        <div className="stage"><div className="stage-grid" /></div>
        <div className="notice">
          <div className="ended">
            {/* The horizon they arrived on. The join screen opens with this
                skyline, so the tour closes on it too — the same place,
                seen once more on the way out. Also, plainly: a screen with
                nothing on it reads as a failure, whatever it says. */}
            <div className="ended-art" aria-hidden="true">
              <svg viewBox="0 0 800 160" preserveAspectRatio="none" fill="currentColor">
                <path d="M40 160V96l42-30 42 30v64Z" /><path d="M96 160V112l34-24 34 24v48Z" />
                <path d="M210 160V88l48-34 48 34v72Z" /><path d="M286 160V118l30-22 30 22v42Z" />
                <path d="M392 160V104l40-28 40 28v56Z" />
                <path d="M498 160V92l46-32 46 32v68Z" /><path d="M572 160V120l28-20 28 20v40Z" />
                <path d="M664 160V108l38-26 38 26v52Z" />
                <rect className="ended-win" x="66" y="112" width="9" height="11" rx="1" />
                <rect className="ended-win" x="240" y="106" width="10" height="12" rx="1" />
                <rect className="ended-win" x="266" y="106" width="10" height="12" rx="1" />
                <rect className="ended-win" x="418" y="120" width="9" height="11" rx="1" />
                <rect className="ended-win" x="524" y="110" width="10" height="12" rx="1" />
                <rect className="ended-win" x="690" y="124" width="9" height="11" rx="1" />
              </svg>
            </div>

            {project?.agency_public?.logo ? (
              <img className="ended-logo" src={project.agency_public.logo}
                   alt={project?.agency_public?.name || facts.agency || "Agency"} />
            ) : null}
            <h1>This tour has ended</h1>
            {/* Everything to be said comes BEFORE the action. Copy after a
                button is read by nobody, and reads as an afterthought by
                everybody else. */}
            <p>
              {firstNameOf(hostName) || "Your advisor"} closed the room
              {facts.name ? <> — thanks for walking through <b>{facts.name}</b></> : null}.
              {keepLookingHref ? " Everything you saw is still there." : null}
            </p>
            {/* What they just spent twenty minutes forming an opinion
                about — and what they will repeat to someone else tonight.
                Every value is already on the page; none of it is fetched
                for this. Anything absent simply does not appear, because
                a row reading "Possession: —" is worse than no row. */}
            {(facts.configuration || facts.possession || facts.location || facts.rera) && (
              <dl className="ended-facts">
                {facts.configuration && (
                  <div><dt>Homes</dt><dd>{facts.configuration}</dd></div>
                )}
                {facts.location && (
                  <div><dt>Where</dt><dd>{facts.location}</dd></div>
                )}
                {facts.possession && (
                  <div><dt>Possession</dt><dd>{facts.possession}</dd></div>
                )}
                {facts.rera && (
                  <div><dt>RERA</dt><dd className="mono">{facts.rera}</dd></div>
                )}
              </dl>
            )}

            {keepLookingHref ? (
              <>
                <a className="ended-cta" href={keepLookingHref}>
                  Keep looking around {facts.name || "the project"}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </a>
                {/* The person who decides was usually not on the call. This
                    forwards the AGENT's link, so a home shown to a spouse
                    tonight still credits whoever gave the tour. */}
                <a
                  className="ended-share"
                  href={waLink(undefined, familyShareMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.22 8.22 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.25-8.23 2.2 0 4.27.86 5.83 2.41a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.24 8.23Z" />
                  </svg>
                  Send it to your family
                </a>
              </>
            ) : (
              <p className="ended-sub">
                Ask {firstNameOf(hostName) || "your advisor"} for a new link whenever
                you&apos;d like to look again.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`grg-root${railOn ? " railon" : ""}`}>
      <div className="stage"><div className="stage-grid" /></div>

      {/* ── SAY WHEN THE CONNECTION HAS GONE ──
          Until this existed, the header went on saying LIVE through a
          total outage: voice dead, chat unable to send, the walkthrough
          no longer following the host, and not one word on screen. A
          buyer concludes the advisor went quiet and waits politely
          through the rest of the tour.

          Only rendered when there is something to say — a room that
          announces "connected" every few seconds teaches people to ignore
          the one message that matters. */}
      {connectionNotice(voice.connection) && (
        <div className="grg-conn" role="status">{connectionNotice(voice.connection)}</div>
      )}

      {/* The walkthrough itself. `role=viewer` makes the engine follow
          the host's camera rather than accept this buyer's input — see
          utils/guidedRoomCanvas.ts, including why this URL deliberately
          carries less than the one ProjectDetailsV3 builds.

          A project with no meta model falls back to the card below,
          which explains itself. Voice is live either way, so a room
          without a canvas is still a tour, just an audio one. */}
      <div
        ref={mountRef}
        className={`mount${railOn ? " gutter" : ""}${fullscreen !== "off" ? " fs" : ""}${fullscreen === "fake" ? " fs-fake" : ""}`}
      >
        {canvas && fullscreen !== "off" && (
          <button className="fs-exit" onClick={() => void exitFullscreen()} aria-label="Exit full screen">
            <IcExitFullscreen />
          </button>
        )}
        {canvas ? (
          <iframe
            className="canvas"
            title={`Guided walkthrough of ${facts.name || "the project"}`}
            src={canvas}
            /* No `microphone`: the buyer's mic belongs to Agora, and
               handing it to the engine as well would have two things
               competing for one device. Fullscreen and XR are the
               engine's own controls. */
            allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope"
            allowFullScreen
          />
        ) : (
          <div className="mount-in">
            <div className="mount-mark"><IcPlay /></div>
            <h2>
              {hostName ? `${firstNameOf(hostName)} is showing you around` : "Your tour is running"}
            </h2>
            <p>
              {voice.status === "live"
                ? <>You&apos;re listening to the walkthrough of <b>{facts.name}</b>. Ask anything by voice, and bring your family in with your family link.</>
                : voice.status === "connecting"
                  ? <>Connecting you to <b>{facts.name}</b>…</>
                  : <>You&apos;re in the room for <b>{facts.name}</b>. {voice.error}</>}
              {/* Live, but no microphone. They can HEAR everything — say so,
                  rather than leaving them wondering if the room is broken. */}
              {voice.status === "live" && !!voice.micError && (
                <p className="hintline">
                  <IcInfo />
                  <span>You can hear the tour. {voice.micError}</span>
                </p>
              )}
            </p>
          </div>
        )}
      </div>

      {/* ── header ── */}
      <div className="top">
        <div className="cap t">
          <button className="icb" onClick={() => void leave()} title="Leave tour" aria-label="Leave tour"><IcBack /></button>
          <div className="ct">
            <b>{facts.name}</b>
            <small>
              {facts.configuration && <><span className="ptype">{facts.configuration}</span><span className="dot" /></>}
              {facts.location && <><span className="pin"><IcPin /></span>{facts.location}<span className="dot" /></>}
              Guided tour
              {elapsed && <><span className="dot" /><span className="mono">{elapsed}</span></>}
            </small>
          </div>
          <span className="live"><i />Live</span>
        </div>
        <div className="spacer" />
        {hostName && (
          <div className="cap host-cap">
            <span className="hav">{initialsOf(hostName)}</span>
            <span><span className="hk">Your advisor</span><span className="hn">{hostName}</span></span>
          </div>
        )}
      </div>

      {/* ── rail ── */}
      <div className={`rail${railOn ? "" : " out"}`} aria-hidden={!railOn}>
        <div className="rhead">
          <b>{tab === "about" ? "Project" : tab === "invite" ? "Invite" : tab === "people" ? "People" : "Chat"}</b>
          <button className="rclose" onClick={() => setRailOn(false)} title="Hide panel" aria-label="Hide panel"><IcClose /></button>
        </div>
        <div className="rtabs">
          <button className={`rtab${tab === "about" ? " on" : ""}`} onClick={() => setTab("about")}>Project</button>
          <button className={`rtab${tab === "invite" ? " on" : ""}`} onClick={() => setTab("invite")}>Invite</button>
          <button className={`rtab${tab === "people" ? " on" : ""}`} onClick={() => setTab("people")}>
            People<span className="n">{people.length}</span>
          </button>
          <button className={`rtab${tab === "chat" ? " on" : ""}`} onClick={() => setTab("chat")}>Chat</button>
        </div>

        <div className="rbody">
          {/* ── PROJECT ── */}
          {tab === "about" && (
            <div className="pane">
              <div className="proj">
                <b>{facts.name}</b>
                {(facts.location || facts.configuration) && (
                  <small>
                    {facts.location && <><span className="pin"><IcPin /></span>{facts.location}</>}
                    {facts.location && facts.configuration && <span className="dot" />}
                    {facts.configuration}
                  </small>
                )}
                {(facts.possession || facts.rera) && (
                  <div className="pmeta">
                    {facts.possession && (
                      <div><span><IcCal />Possession</span><b>{facts.possession}</b></div>
                    )}
                    {facts.rera && (
                      <div className="ok"><span><IcShield />RERA</span><b className="mono">{facts.rera}</b></div>
                    )}
                  </div>
                )}
              </div>

              {facts.highlights.length > 0 && (
                <>
                  <div className="shead"><p>Highlights</p></div>
                  {/* Columns follow the count rather than sitting fixed at
                      three: four villa facts in a three-column grid leave one
                      cell stranded on its own row, and five leave a hole. */}
                  <div
                    className="hl"
                    style={{ gridTemplateColumns: `repeat(${highlightColumns(facts.highlights.length)}, minmax(0, 1fr))` }}
                  >
                    {facts.highlights.map((h) => (
                      <div className="s" key={h.label}>
                        <div className="v">{h.value}{h.unit && <i>{h.unit}</i>}</div>
                        <span className="k">{h.label}</span>
                        {h.note && <span className="n">{h.note}</span>}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {facts.location && (
                <>
                  <div className="shead"><p>Location</p><span>{facts.location}</span></div>
                  {/* A real map when we have coordinates. This was a CSS grid
                      with a dot pinned at 50%/44% — decorative, identical for
                      every project, and telling a buyer nothing about where
                      the property is. The Expand overlay has always rendered
                      the actual embed; there was no reason the panel couldn't.

                      Not interactive: the iframe would swallow scroll inside a
                      panel the buyer is scrolling, and dragging a 160px map is
                      no use anyway. Expand opens the one you can move. */}
                  <div className="map">
                    {mapImage ? (
                      <img src={mapImage} alt={`Map showing ${facts.location}`} loading="lazy" />
                    ) : (
                      /* No key or no coordinates — keep the abstract panel
                         rather than a broken image or an empty grey box. It
                         promises nothing, which is the honest thing when we
                         don't know where the property is. */
                      <i className="g" />
                    )}
                    {/* Ours, not Google's. The image carries no marker, so the
                        dot is drawn dead centre over the coordinates the map
                        is centred on. */}
                    <i className="mk" />
                    <b>{facts.location}</b>
                    {facts.geo && (
                      <button className="go" onClick={() => setOverlay("map")}><IcExpand />Expand</button>
                    )}
                  </div>
                </>
              )}

              {facts.units.length > 0 && (
                <>
                  <div className="shead">
                    <p>Unit types</p>
                    <span>
                      {facts.units.reduce((n, u) => n + (u.count || 0), 0) || ""}
                      {facts.units.some((u) => u.count !== null) ? " homes" : ""}
                    </span>
                  </div>
                  <div className="units">
                    {facts.units.map((u) => (
                      <button
                        key={u.key}
                        className={`u${selectedUnit === u.key ? " on" : ""}`}
                        onClick={() => setUnitKey(u.key)}
                      >
                        <em /><b>{u.label}</b>
                        {u.count !== null && <span>{u.count}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {facts.amenityTotal > 0 && (
                <>
                  <div className="amhead">
                    <p>Amenities</p>
                    <button className="amall" onClick={() => setOverlay("amenities")}>
                      All {facts.amenityTotal} {cp(0x2192)}
                    </button>
                  </div>
                  <div className="am">
                    {facts.groups.flatMap((g) => g.items).slice(0, 6).map((a) => (
                      <div key={a}><IcTick />{a}</div>
                    ))}
                  </div>
                </>
              )}

              {facts.brochure && (
                <>
                  <div className="shead"><p>Brochure</p><span>PDF</span></div>
                  <div className="broch">
                    <button className="pri" onClick={() => window.open(facts.brochure, "_blank", "noopener")}>
                      <IcEye />View
                    </button>
                    <a
                      className=""
                      href={facts.brochure}
                      download
                      style={{ flex: 1, display: "inline-flex" }}
                    >
                      <button style={{ width: "100%" }}><IcDown />Download</button>
                    </a>
                  </div>
                </>
              )}

              <p className="hintline">
                <IcInfo />
                <span>
                  Ask {firstNameOf(hostName) || "your advisor"} anything about these during the walkthrough.
                </span>
              </p>
            </div>
          )}

          {/* ── INVITE ── */}
          {tab === "invite" && (
            <div className="pane">
              {mayInviteFamily && (
              <section className="card fam">
                <header className="card-h">
                  <span className="card-i"><IcFamily /></span>
                  <span className="card-t"><b>Your family</b><small>Straight in, no verification</small></span>
                  <span className="card-q">{quota.label}</span>
                </header>
                <div className="card-b">
                  <span className="qbar">
                    {quota.segments.map((used, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <i key={i} className={`qseg${used ? " used" : ""}`} />
                    ))}
                  </span>
                  <div className="linkbox"><span>{famUrl || "Preparing your family link…"}</span></div>
                  <div className="btnrow">
                    <button
                      className={`cp${copied === "fam" ? " done" : ""}`}
                      disabled={!famUrl || quota.exhausted}
                      onClick={() => void copy(famUrl, "fam")}
                    >
                      {copied === "fam" ? <IcTick /> : <IcCopy />}{copied === "fam" ? "Copied" : "Copy"}
                    </button>
                    <button
                      className="wa"
                      disabled={!famUrl || quota.exhausted}
                      onClick={() => window.open(waLink(undefined, famMessage()), "_blank", "noopener")}
                    >
                      <IcWa />Send to family
                    </button>
                  </div>
                  <p className="hintline">
                    <IcTick /><span>{familyNote(quota.used, quota.max)}</span>
                  </p>
                  {family.length > 0 ? (
                    family.map((f: any) => (
                      <div className="pass" key={f.session_id}>
                        <span className="pav">{initialsOf(String(f.display_name || "Guest"))}</span>
                        <span className="pi">
                          <b>{f.display_name || "Guest"}</b>
                          <small className="in">In the room</small>
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="empty">Nobody has used it yet. They&apos;ll appear here as they join.</div>
                  )}
                </div>
              </section>
              )}

              {/* Not an invited lead: the friends link is the only route they
                  can offer, and it keeps the host in control of who arrives.
                  No explanation of what they're missing — a buyer shouldn't be
                  told about a privilege they don't have.

                  Gated on the link existing. A card whose whole content is a
                  link, showing a dash and two dead buttons, tells the buyer
                  the room is broken; nothing at all tells them the truth,
                  which is that this door isn't theirs to open. */}
              {!!rawUrl && (
              <section className="card">
                <header className="card-h">
                  <span className="card-i"><IcLink /></span>
                  <span className="card-t">
                    <b>Friends &amp; colleagues</b>
                    <small>They verify, then {firstNameOf(hostName) || "the host"} admits</small>
                  </span>
                </header>
                <div className="card-b">
                  <div className="linkbox"><span>{rawUrl || "—"}</span></div>
                  <div className="btnrow">
                    <button
                      className={`cp${copied === "raw" ? " done" : ""}`}
                      disabled={!rawUrl}
                      onClick={() => void copy(rawUrl, "raw")}
                    >
                      {copied === "raw" ? <IcTick /> : <IcCopy />}{copied === "raw" ? "Copied" : "Copy"}
                    </button>
                    <button
                      className="wa ghost"
                      disabled={!rawUrl}
                      onClick={() => window.open(waLink(undefined, rawMessage()), "_blank", "noopener")}
                    >
                      <IcWa />Send on WhatsApp
                    </button>
                  </div>
                  <p className="hintline warn">
                    <IcInfo />
                    <span>
                      They enter their mobile and wait in the lobby until{" "}
                      {firstNameOf(hostName) || "the host"} lets them in.
                    </span>
                  </p>
                </div>
              </section>
              )}

              {/* Both doors closed — a family guest, admitted on someone
                  else's pass. Says who to ask rather than leaving a blank
                  panel that reads as a page that failed to load. */}
              {!mayInviteFamily && !rawUrl && (
                <div className="empty">
                  Ask {firstNameOf(hostName) || "your advisor"} to invite anyone
                  else you'd like on this tour — they can let them in from their side.
                </div>
              )}
            </div>
          )}

          {/* ── PEOPLE ── */}
          {tab === "people" && (
            <div className="pane">
              <p className="lbl">In the room</p>
              {people.map((p) => (
                <div className={`row${voice.speaking[p.rtc_uid || p.session_id] ? " talking" : ""}`} key={p.session_id}>
                  <span className={`av ${p.avatar === "em" ? "" : p.avatar}`}>{initialsOf(p.name)}</span>
                  <span className="who">
                    <b>{p.name}</b>
                    {p.tag && (
                      <span className={`tag ${p.tag === "Host" ? "host" : p.tag === "You" ? "you" : "fam"}`}>
                        {p.tag}
                      </span>
                    )}
                    {p.sub && <small>{p.sub}</small>}
                  </span>
                </div>
              ))}
              {/* No empty state: knownRoster() always returns at least the
                  host and you, so "Just you so far" was only ever shown
                  because the lobby 401'd — a backend fact dressed up as a
                  fact about the room. */}
            </div>
          )}

          {/* ── CHAT ── */}
          {tab === "chat" && (
            <div className="pane chat">
              <div className="log" ref={logRef}>
                <div className="msg sys"><p>You joined the tour</p></div>
                {/* Said once, plainly. Nothing is stored anywhere — see
                    utils/guidedRoomChat.ts — and a buyer who assumes their
                    advisor can read this back tomorrow would be wrong in a
                    way that costs them. */}
                <div className="msg sys">
                  {/* This said messages "aren't saved after it ends" until
                      2026-09-10. That stopped being true the moment chat
                      persistence started working, and it is the worst
                      possible sentence to leave stale: it sits beside a
                      recording consent notice, so a buyer reading both was
                      told we keep the voice and discard the typing, while
                      we kept both. Being wrong in the reassuring direction
                      about someone's own words is not a copy nit. */}
                  <p>
                    Messages reach everyone in the room, and are kept with the tour so{" "}
                    {firstNameOf(hostName) || "your advisor"} can follow up on what you asked.
                  </p>
                </div>
                {chat.messages.map((m) => {
                  const mine = m.sessionId === sid;
                  const stamp = chatTime(m.at);
                  return (
                    <div className={`msg${mine ? " me" : ""}`} key={m.id}>
                      <b>
                        {mine ? "You" : nameFor(m.sessionId, m.name)}
                        {!mine && m.role === "guide" ? " · your advisor" : ""}
                        {stamp ? ` · ${stamp}` : ""}
                      </b>
                      <p>{m.text}</p>
                    </div>
                  );
                })}
                {!!chat.unavailable && (
                  <div className="msg sys"><p>{chat.unavailable}</p></div>
                )}
                {sendFailed && (
                  <div className="msg sys">
                    <p>
                      That message didn&apos;t send. Try again, or just ask{" "}
                      {firstNameOf(hostName) || "your advisor"} out loud.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* The composer exists only on the Chat tab, per the spec. Still
            disabled rather than hidden while voice is connecting: a buyer
            who taps Chat should learn WHY they can't type yet, not find a
            box that swallows what they wrote. */}
        {tab === "chat" && (
          <form className="compose" onSubmit={(e) => void onSend(e)}>
            <input
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setSendFailed(false); }}
              disabled={!chat.ready || sending}
              placeholder={
                chat.unavailable ? chat.unavailable
                  : chat.ready ? "Message the room"
                    : "Connecting…"
              }
              aria-label="Message the room"
            />
            <button type="submit" disabled={!chat.ready || sending || !draft.trim()} aria-label="Send">
              <IcSend />
            </button>
          </form>
        )}
      </div>

      {/* ── dock ── */}
      <div className="dock">
        <button
          className={`ctl${voice.canPublish && !voice.micOn ? " off" : voice.micOn ? " on" : ""}`}
          disabled={voice.status !== "live" || !voice.canPublish || !!voice.micError}
          aria-pressed={voice.micOn}
          aria-label={voice.micOn ? "Mute your microphone" : "Unmute your microphone"}
          data-tip={
            voice.status !== "live" ? (voice.error || "Connecting voice…")
              : voice.micError ? voice.micError
                : !voice.canPublish ? "You're listening only"
                  : voice.micOn ? "Mute" : "Unmute"
          }
          onClick={() => void voice.toggleMic()}
        >
          {voice.micOn ? <IcMic /> : <IcMicOff />}
        </button>
        <button className="ctl" disabled data-tip="Voice only — video would compete with the walkthrough">
          <IcCam />
        </button>
        {/* Disabled where no relay is configured rather than silently
            doing nothing — see relayUrl(). Only one relay exists today
            and it's the dev one; a buyer on prod pressing this would
            otherwise get a button that looks pressed and reaches nobody. */}
        <button
          className={`ctl${hand ? " on" : ""}`}
          disabled={!relay.available}
          data-tip={
            !relay.available ? "Raising your hand isn't available yet — just speak up"
              : hand ? "Lower your hand"
                : "Raise your hand"
          }
          onClick={() => { const next = !hand; setHand(next); relay.sendHand(next); }}
          aria-pressed={hand}
          aria-label={hand ? "Lower your hand" : "Raise your hand"}
        >
          <IcHand />
        </button>
        <div className="sep" />
        <button className={`ctl${railOn && tab === "about" ? " on" : ""}`} onClick={() => openTab("about")} data-tip="Project details" aria-label="Project details">
          <IcSkyline />
        </button>
        <button className={`ctl${railOn && tab === "invite" ? " on" : ""}`} onClick={() => openTab("invite")} data-tip="Invite" aria-label="Invite">
          <IcInvite />
          {family.length > 0 && <span className="badge">{family.length}</span>}
        </button>
        <button className={`ctl${railOn && tab === "people" ? " on" : ""}`} onClick={() => openTab("people")} data-tip="People" aria-label="People">
          <IcPeople />
        </button>
        <button className={`ctl${railOn && tab === "chat" ? " on" : ""}`} onClick={() => openTab("chat")} data-tip="Chat" aria-label="Chat">
          {chat.unread > 0 && <span className="badge">{chat.unread}</span>}
          <IcChat />
        </button>
        {canvas && (
          <button
            className={`ctl${fullscreen !== "off" ? " on" : ""}`}
            onClick={() => void (fullscreen === "off" ? enterFullscreen() : exitFullscreen())}
            data-tip={fullscreen === "off" ? "Full screen" : "Exit full screen"}
            aria-label={fullscreen === "off" ? "Full screen" : "Exit full screen"}
          >
            {fullscreen === "off" ? <IcFullscreen /> : <IcExitFullscreen />}
          </button>
        )}
        <div className="sep" />
        <button className="ctl leave" onClick={() => void leave()}><IcLeave />Leave</button>
      </div>

      {/* ── overlays ── */}
      {overlay === "map" && facts.geo && (
        <div className="modal" onClick={() => setOverlay(null)} role="dialog" aria-label="Location map">
          <button className="mclose" onClick={() => setOverlay(null)} aria-label="Close"><IcClose /></button>
          <div className="mbig" onClick={(e) => e.stopPropagation()}>
            <iframe
              title="Project location"
              loading="lazy"
              src={`https://www.google.com/maps?q=${facts.geo[0]},${facts.geo[1]}&z=15&output=embed`}
            />
            <div className="card2">
              <b>{facts.name}</b>
              <small>{facts.location}</small>
            </div>
          </div>
        </div>
      )}

      {overlay === "amenities" && (
        <div className="modal" onClick={() => setOverlay(null)} role="dialog" aria-label="All amenities">
          <button className="mclose" onClick={() => setOverlay(null)} aria-label="Close"><IcClose /></button>
          <div className="ambox" onClick={(e) => e.stopPropagation()}>
            <header>
              <b>Amenities</b>
              <small>
                {facts.amenityTotal} across {facts.groups.length}{" "}
                {facts.groups.length === 1 ? "group" : "groups"} {cp(0x00b7)} {facts.name}
              </small>
            </header>
            <div className="ambody">
              {facts.groups.map((g) => (
                <section className="amgrp" key={g.category}>
                  <p>{g.label}<i>{g.items.length}</i></p>
                  <ul>{g.items.map((a) => <li key={a}>{a}</li>)}</ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedRoomGuest;
export { GuidedRoomGuest };
